/**
 * Flux Agent HTTP client and its wire types. Every call goes through the shared credentialed
 * `request()` helper, so mutations carry the CSRF marker and a retry-stable idempotency key; this
 * module only shapes request bodies for the `/flux-agent` routes and folds the response envelope
 * into a discriminated result with a user-presentable message. Trainer-only routes are exposed here
 * as well, but the server enforces trainer authority; callers must still render them only when
 * `status.trainer` is true so the secret system prompt never appears in coaching UI.
 *
 * @module apps/web/src/lib/fluxAgentApi
 */

import type { StructureResponse } from '@fluxability/types';
import { API_BASE, request } from '@/lib/api';

/** Conversation flavour: ordinary coaching, or a trainer coaching the agent's global playbooks. */
export type FluxAgentMode = 'coach' | 'trainer';

/** Server capabilities and limits for the signed-in user. */
export interface FluxAgentStatus {
  /** False when no model provider is configured; the page then shows an empty state. */
  enabled: boolean;
  /** Provider identifier such as `deepseek` or `workers-ai`. */
  provider: string;
  /** Model identifier reported by the provider. */
  model: string;
  /** True only for framework trainers, who may open trainer mode and the console. */
  trainer: boolean;
  /** Which speech routes are available. */
  voice: {
    /** True when `/speech/transcribe` accepts recorded audio for dictation and voice mode. */
    transcribe: boolean;
    /** True when `/speech/synthesize` can read replies aloud; voice mode stays text-only otherwise. */
    synthesize: boolean;
    /**
     * True when voice mode can talk to OpenAI GPT-Live (`POST /conversations/:id/live/session`).
     * Absent on older deployments, which means false: voice mode uses the transcribe, chat and
     * speak pipeline.
     */
    live?: boolean;
  };
  /**
   * True when the agent can search the web (an OpenAI key on an OpenAI host). Absent on older
   * deployments, which means false.
   */
  search?: boolean;
  /** Upper bounds the client checks before sending. */
  limits: {
    /** Longest message body the server accepts, in characters (8,000 by default). */
    maxMessageChars: number;
    /** Largest file upload the server accepts, in raw bytes before base64 encoding. */
    maxFileBytes: number;
    /** Largest recording the server transcribes, in raw bytes before base64 encoding. */
    maxAudioBytes: number;
  };
  /** Photo attachments; absent on deployments without the media store. */
  photos?: {
    /** True when the configured model sees images; otherwise it is told a photo arrived unseen. */
    vision: boolean;
    /** Largest photo upload the server accepts, in bytes (5 MiB). */
    maxPhotoBytes: number;
    /** Most photos one message may carry (4). */
    maxPhotosPerMessage: number;
  };
}

/** One stored conversation summary. */
export interface FluxAgentConversation {
  /** Server-assigned conversation id used in `/conversations/:id` routes. */
  id: string;
  /** Display title; the server replaces the default with a 60-character excerpt of the first message. */
  title: string;
  /** Fixed at creation; trainer conversations may edit global playbooks. */
  mode: FluxAgentMode;
  /** UTC creation timestamp as stored by D1 (`YYYY-MM-DD HH:MM:SS`). */
  createdAt: string;
  /** UTC timestamp of the latest message, used to order the conversation list. */
  updatedAt: string;
  /** For a coaching side chat, the conversation it coaches; `null` for ordinary conversations. */
  coachesConversationId: string | null;
}

/** Category of a background tool call, used to choose its icon and post-reply refreshes. */
export type FluxAgentActionKind =
  | 'schedule'
  | 'memory'
  | 'playbook'
  | 'system_prompt'
  | 'read'
  | 'field_suggestion'
  | 'timing_cue'
  | 'proposal'
  | 'workspace'
  | 'session'
  | 'confirm'
  | 'value'
  | 'structure'
  | 'mcp'
  | 'search'
  | 'trigger'
  | 'view'
  | 'sound'
  | 'energy';

/** A stored photo or audio clip from the Flux Agent media store, served owner-only at `url`. */
export interface FluxAgentMedia {
  /** Server-assigned id; photos are attached to a message by sending it in `mediaIds`. */
  id: string;
  /** `photo` (attached by the person) or `audio` (a spoken clip or sound cue). */
  kind: 'photo' | 'audio';
  /** `photo`, `sound_clip` or `sound_cue`. */
  source: string;
  /** Content type settled by the server from the bytes. */
  mimeType: string;
  /** Stored size in bytes. */
  sizeBytes: number;
  /** Photo width in pixels; `null` for audio. */
  width: number | null;
  /** Photo height in pixels; `null` for audio. */
  height: number | null;
  /** Audio length in milliseconds; `null` for photos or when unknown. */
  durationMs: number | null;
  /** Card title of an audio item; `null` for photos. */
  label: string | null;
  /** Conversation the item belongs to, once linked. */
  conversationId: string | null;
  /** UTC creation timestamp as stored by D1. */
  createdAt: string;
  /** Same-origin path that serves the bytes to their owner. */
  url: string;
}

/** What a Flux Agent form creates or edits. */
export type FluxAgentFormKind = 'activity' | 'bundle' | 'project' | 'custom_field' | 'trigger';

/** One field of a form Flux Agent fills in, already formatted by the server. */
export interface FluxAgentFormField {
  /** Stable argument path such as `title` or `stateProfile.resistance`. */
  key: string;
  /** Label as the app's own form shows it. */
  label: string;
  /** Formatted value; empty when the agent has not filled it. */
  display: string;
  /** True when the agent supplied a value. */
  filled: boolean;
  /** True for the field the agent is writing right now (live forms only). */
  active?: boolean;
  /** For custom fields: whether the field is mandatory. */
  badge?: 'required' | 'optional';
}

/**
 * A create or edit form shown in the chat: live while the agent writes it (`filling`), then the
 * final state of the call. Field lists are shaped by the server (`flux_agent::forms`).
 */
export interface FluxAgentForm {
  /** What the form creates or edits. */
  kind: FluxAgentFormKind;
  /** Server tool name, such as `create_activity`. */
  tool: string;
  /** Card heading, such as `New activity`. */
  heading: string;
  /** `filling` while streaming, then `completed`, `failed` or `pending`. */
  status: 'filling' | 'completed' | 'failed' | 'pending';
  /** Every field of the form in display order. */
  fields: FluxAgentFormField[];
  /** Activity custom field values with their Required or Optional badges. */
  customFields?: FluxAgentFormField[];
  /** Id of the created or edited item, once saved. */
  entityId?: string;
  /** In-app path that opens the item, once saved. */
  link?: string;
}

/** A live form for one streamed tool call, keyed by its `<round>:<index>` slot. */
export interface FluxAgentLiveForm {
  /** Slot of the tool call; the final action with the same slot replaces this form. */
  slot: string;
  /** The form as filled so far. */
  form: FluxAgentForm;
}

/** A background tool call the agent made while producing a reply. */
export interface FluxAgentAction {
  /** Server tool name that ran, such as a schedule or memory tool. */
  tool: string;
  /** Short human-readable label shown as the action card heading. */
  title: string;
  /**
   * Whether the tool call succeeded; failed actions are shown but trigger no refresh. A
   * confirmation card is `pending` until the person confirms or cancels it.
   */
  status: 'completed' | 'failed' | 'pending' | 'cancelled';
  /** One-line description of what the tool did or why it failed. */
  summary: string;
  /** Category that selects the card icon and which client data to refresh. */
  kind: FluxAgentActionKind;
  /** Stored learning insight id for a field suggestion, used by its Add button. */
  insightId?: string;
  /** The filled form for creates and edits of activities, bundles, projects and custom fields. */
  form?: FluxAgentForm;
  /** `<round>:<index>` of the tool call, matching the live form it replaces. */
  slot?: string;
  /** Staged action id of a confirmation card, passed to `confirmAction` or `cancelAction`. */
  pendingId?: string;
  /** For `structure` cards: the Project/Bundle tree (or proposal) and its findings. */
  structure?: StructureResponse;
  /** For `search` cards: the web sources the answer used, at most 8, cited ones first. */
  sources?: FluxAgentSearchSource[];
  /** Owner-scoped generated view opened only after a user click. */
  viewId?: string;
  /** For `sound` cards: the stored clip or cue the card plays. */
  media?: FluxAgentMedia;
}

/** One web page a `search` card lists. Untrusted text from the public web; render as plain text. */
export interface FluxAgentSearchSource {
  /** Page title, or its host when the page had none; at most 160 characters. */
  title: string;
  /** Link target the server already limited to http and https; the card checks it again. */
  url: string;
  /** Passage of the answer this page supports, at most 240 characters; absent for pages only consulted. */
  snippet?: string;
}

/** A stored chat message; `actions` is only populated on assistant messages. */
export interface FluxAgentMessage {
  /** Server-assigned message id. */
  id: string;
  /** Author of the visible message; tool transcript rows are never returned. */
  role: 'user' | 'assistant';
  /** Message text; assistant replies are Markdown rendered by `FluxAgentMarkdown`. */
  content: string;
  /** UTC timestamp as stored by D1 (`YYYY-MM-DD HH:MM:SS`). */
  createdAt: string;
  /** Tool calls made while producing this reply; empty for user messages. */
  actions: FluxAgentAction[];
  /** Files attached to a user message, shown as chips; empty when none were attached. */
  files: {
    /** Id of the uploaded {@link FluxAgentFile}. */
    id: string;
    /** File name captured when the message was sent. */
    name: string;
    /** `photo` for a photo from the media store; absent for an uploaded text file. */
    kind?: 'photo';
    /** For a photo: the same-origin path that serves it to its owner. */
    url?: string;
    /** For a photo: its width in pixels. */
    width?: number | null;
    /** For a photo: its height in pixels. */
    height?: number | null;
  }[];
}

/** An uploaded file the agent can read when its id is attached to a message. */
export interface FluxAgentFile {
  /** Server-assigned id passed in `fileIds` when sending a message. */
  id: string;
  /** Original file name (1-255 characters). */
  name: string;
  /** MIME type sent at upload, used by the server to choose text extraction. */
  mimeType: string;
  /** Size of the original upload in bytes; the bytes themselves are not retained. */
  sizeBytes: number;
  /** Length of the extracted text the agent can read, in characters. */
  chars: number;
  /** UTC upload timestamp as stored by D1 (`YYYY-MM-DD HH:MM:SS`). */
  createdAt: string;
}

/** A versioned per-user memory document; trainer files use {@link LibraryDocument} instead. */
export interface FluxAgentDocument {
  /** Relative Markdown path such as `memory/profile.md`. */
  path: string;
  /** Full Markdown body (at most 200,000 characters). */
  content: string;
  /** Monotonic version starting at 1; send it back as `expectedVersion` to detect conflicts. */
  version: number;
  /** UTC timestamp of the latest write as stored by D1 (`YYYY-MM-DD HH:MM:SS`). */
  updatedAt: string;
  /** Email of the last editor, `flux-agent` for agent writes, or null when unknown. */
  updatedBy: string | null;
}

/**
 * A trainer-managed file in the Flux Agent library Git repository: `system-prompt.md` at the root or
 * a playbook under `playbooks/`.
 */
export interface LibraryDocument {
  /** Repository path such as `system-prompt.md` or `playbooks/errands.md`. */
  path: string;
  /** Full Markdown body (at most 200,000 characters). */
  content: string;
  /**
   * Git blob id of the current content; send it back as `expectedRevision` to detect conflicts.
   * Null only when the system prompt is the built-in default (or for an unsaved new playbook).
   */
  revision: string | null;
}

/** Origin of a library commit; `git` means it was pushed directly with git. */
export type LibraryRevisionSource = 'agent' | 'editor' | 'import' | 'seed' | 'coach' | 'git';

/** One commit that touched a library file, newest first in history listings. */
export interface LibraryRevision {
  /** Full Git commit id; the console shows the first 7 characters. */
  commit: string;
  /** Full Markdown body at that commit; empty for a deletion. */
  content: string;
  /** True when this commit deleted the file; deleted revisions cannot be restored. */
  deleted: boolean;
  /** Commit change note, or null when none was given. */
  changeNote: string | null;
  /** Where the commit came from: agent tool, console editor, import, seed, or a direct git push. */
  source: LibraryRevisionSource;
  /** Author email of the commit, or null when unknown. */
  updatedBy: string | null;
  /** Commit timestamp as ISO-8601. */
  createdAt: string;
}

/** State of the trainer library Git repository returned by `GET /admin/library`. */
export interface LibraryInfo {
  /** False when Git storage (Cloudflare Artifacts) is not configured on the server. */
  configured: boolean;
  /** Repository name. */
  repository: string;
  /** Branch the agent reads and every change is committed to; always `main`. */
  branch: 'main';
  /** HTTPS clone URL, or null when the repository is not configured. */
  remote: string | null;
  /** Latest commit id on `main`, or null when the repository has no commits yet. */
  commit: string | null;
}

/** Access level of a short-lived library Git token. */
export type LibraryTokenScope = 'read' | 'write';

/** A short-lived Git credential for the library repository. Never persist it. */
export interface LibraryToken {
  /** HTTPS clone URL the token is valid for. */
  remote: string;
  /** Bearer secret, sent as `Authorization: Bearer <token>` through `http.extraHeader`. */
  token: string;
  /** ISO-8601 expiry, one hour after creation. */
  expiresAt: string;
  /** Whether the token may push (`write`) or only clone and pull (`read`). */
  scope: LibraryTokenScope;
}

/** Envelope-free outcome of a Flux Agent call. Failures keep the HTTP status and server code. */
export type FluxAgentResult<T> =
  | {
      /** Discriminant for a successful call. */
      ok: true;
      /** Unwrapped response payload; never null or undefined. */
      data: T;
    }
  | {
      /** Discriminant for a failed call. */
      ok: false;
      /** HTTP status when a response arrived; absent for network failures and timeouts. */
      status?: number;
      /** Server error code, `EMPTY_RESPONSE`, or `UNKNOWN_ERROR` when none was supplied. */
      code: string;
      /** User-presentable copy: friendly text for known codes, else the server message. */
      message: string;
    };

/** Minimal shape of the shared `request()` result consumed by {@link toFluxAgentResult}. */
export interface FluxAgentEnvelope<T> {
  /** HTTP status when the shared helper received a response. */
  status?: number;
  /** Whether the server reported success. */
  success: boolean;
  /** Payload on success; a missing value becomes an `EMPTY_RESPONSE` failure. */
  data?: T;
  /** Server failure details, absent on success. */
  error?: {
    /** Machine-readable error code such as `FLUX_AGENT_RATE_LIMIT`. */
    code: string;
    /** Server-provided explanation, used when no friendly copy exists for `code`. */
    message: string;
  };
}

/** Server error code for optimistic-concurrency failures on memory and library writes. */
export const FLUX_AGENT_VERSION_CONFLICT = 'FLUX_AGENT_VERSION_CONFLICT';

/** A system-prompt or playbook change staged in a coaching side chat, not yet live. */
export interface CoachingProposal {
  /** `system-prompt.md` or a playbook path under `playbooks/`. */
  path: string;
  /** What the trainer taught, as the agent summarised it. */
  changeNote: string;
  /** True when the proposal deletes the playbook. */
  deleted: boolean;
  /** Unified diff from the live file to the proposed text, computed by the server. */
  diff: string;
  /** Lines added. */
  added: number;
  /** Lines removed. */
  removed: number;
  /** Validation advice that does not block accepting. */
  warnings: string[];
  /** Git blob id the change was drafted against, or `absent`. */
  baseVersion: string;
  /** Git blob id live now, or `absent`. */
  liveVersion: string;
  /** True when the live file changed since drafting; accepting will fail with a conflict. */
  stale: boolean;
  /** UTC timestamp of the latest revision of this proposal. */
  updatedAt: string;
}

/** A coaching side chat opened beside a conversation. */
export interface CoachingSession {
  /** The side chat (a trainer-mode conversation linked to the coached one). */
  conversation: FluxAgentConversation;
  /** Its visible messages. */
  messages: FluxAgentMessage[];
  /** Its staged proposals. */
  proposals: CoachingProposal[];
}

/** Message routes run tools before replying; allow well past the documented 60 s ceiling. */
const MESSAGE_TIMEOUT_MS = 90_000;

/** Product copy that replaces the server message for known error codes. */
const FRIENDLY_MESSAGES: Record<string, string> = {
  FLUX_AGENT_VERSION_CONFLICT:
    'Someone saved a newer version while you were editing. Reload the latest version, then reapply your change.',
  FLUX_AGENT_RATE_LIMIT: 'Flux Agent needs a short breather. Try again in a minute.',
  FLUX_AGENT_UNAVAILABLE:
    'Flux Agent is temporarily unavailable. Your message was kept so you can retry.',
  FLUX_AGENT_TRAINER_ONLY: 'Only framework trainers can do that.',
  FLUX_AGENT_LIBRARY_UNAVAILABLE:
    'The trainer library (Git storage) is not configured or cannot be reached right now. Try again shortly.',
  REQUEST_TIMEOUT:
    'Flux Agent is taking longer than expected. Retry to pick up the reply; the request is safe to resend.',
};

/**
 * Fold a shared-helper response into a {@link FluxAgentResult}. A success without `data` is
 * treated as a malformed response rather than silently yielding `undefined`. Known Flux Agent
 * codes get product copy; any other server message is passed through.
 */
export function toFluxAgentResult<T>(response: FluxAgentEnvelope<T>): FluxAgentResult<T> {
  if (response.success && response.data !== undefined && response.data !== null) {
    return { ok: true, data: response.data };
  }
  if (response.success) {
    return {
      ok: false,
      status: response.status,
      code: 'EMPTY_RESPONSE',
      message: 'Flux Agent returned an empty response.',
    };
  }
  const code = response.error?.code || 'UNKNOWN_ERROR';
  return {
    ok: false,
    status: response.status,
    code,
    message: FRIENDLY_MESSAGES[code] || response.error?.message || 'Something went wrong.',
  };
}

/** True when a failure is the optimistic-concurrency conflict for document saves. */
export function isVersionConflict(result: FluxAgentResult<unknown>): boolean {
  return !result.ok && (result.code === FLUX_AGENT_VERSION_CONFLICT || result.status === 409);
}

/** First 7 characters of a Git object id, as `git log --oneline` shows it. */
export function shortId(id: string): string {
  return id.slice(0, 7);
}

/**
 * Replace the confirmation card whose `pendingId` matches `card` in every message, keeping its
 * slot, so a confirmed or cancelled card shows its outcome without reloading.
 */
export function withResolvedCard(
  messages: FluxAgentMessage[],
  card: FluxAgentAction
): FluxAgentMessage[] {
  return messages.map(message =>
    message.actions?.some(action => action.pendingId && action.pendingId === card.pendingId)
      ? {
          ...message,
          actions: message.actions.map(action =>
            action.pendingId === card.pendingId ? { ...card, slot: action.slot } : action
          ),
        }
      : message
  );
}

/** True when any completed action in the reply moved something on the schedule or changed activities (applying a value, undoing one, or reorganizing Projects and Bundles). */
export function hasCompletedScheduleAction(messages: FluxAgentMessage[]): boolean {
  return messages.some(message =>
    message.actions?.some(
      action =>
        action.status === 'completed' &&
        (action.kind === 'schedule' ||
          action.tool === 'apply_value' ||
          action.tool === 'undo_value_application' ||
          action.tool === 'reorganize_structure')
    )
  );
}

/** The user's IANA time zone, falling back to UTC when the runtime cannot resolve one. */
export function currentTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Send a request to `/flux-agent{endpoint}` through the shared credentialed helper and fold the
 * envelope into a {@link FluxAgentResult}. Never throws for HTTP or network failures.
 */
async function call<T>(endpoint: string, options: RequestInit = {}): Promise<FluxAgentResult<T>> {
  return toFluxAgentResult(await request<T>(`/flux-agent${endpoint}`, options));
}

/** Build `RequestInit` with the given method and a JSON-serialized body. */
function json(method: string, body: unknown): RequestInit {
  return { method, body: JSON.stringify(body) };
}

/** Encode a document path as the `?path=` query string used by delete and history routes. */
function pathQuery(path: string): string {
  return `?path=${encodeURIComponent(path)}`;
}

/** Body accepted by library save routes; `expectedRevision` enables conflict detection. */
export interface LibraryDocumentSave {
  /** New full Markdown body (at most 200,000 characters). */
  content: string;
  /**
   * Blob id the editor started from. Omitted: overwrite unconditionally. Empty string: the file
   * must not exist yet. Any other value must equal the current revision, otherwise the server
   * returns `FLUX_AGENT_VERSION_CONFLICT`.
   */
  expectedRevision?: string;
  /** Optional note recorded in the commit message. */
  changeNote?: string;
}

/** Stored turn returned by both message routes: the conversation and its new visible messages. */
export interface FluxAgentTurn {
  /** Conversation after the turn, with any new title and `updatedAt`. */
  conversation: FluxAgentConversation;
  /** The stored user message followed by the assistant reply. */
  messages: FluxAgentMessage[];
}

/** One parsed server-sent event. */
export interface FluxAgentStreamEvent {
  /** Event name from the `event:` line; `message` when absent. */
  event: string;
  /** The `data:` lines joined with newlines. */
  data: string;
}

/**
 * Incremental `text/event-stream` parser. `push` accepts decoded text split anywhere (including
 * inside `\r\n`) and calls `onEvent` for every complete event; `finish` flushes a final event
 * that lacked its blank line. Comment lines are ignored.
 */
export function createSseParser(onEvent: (event: FluxAgentStreamEvent) => void): {
  /** Feed decoded text. */
  push(text: string): void;
  /** Flush the trailing event at end of stream. */
  finish(): void;
} {
  let buffer = '';
  let event = 'message';
  let data: string[] = [];
  const line = (raw: string) => {
    const text = raw.endsWith('\r') ? raw.slice(0, -1) : raw;
    if (text === '') {
      if (data.length) onEvent({ event, data: data.join('\n') });
      event = 'message';
      data = [];
    } else if (text.startsWith('event:')) event = text.slice(6).trim();
    else if (text.startsWith('data:')) data.push(text.slice(text.startsWith('data: ') ? 6 : 5));
  };
  return {
    push(text) {
      buffer += text;
      let newline = buffer.indexOf('\n');
      while (newline >= 0) {
        line(buffer.slice(0, newline));
        buffer = buffer.slice(newline + 1);
        newline = buffer.indexOf('\n');
      }
    },
    finish() {
      if (buffer) line(buffer);
      buffer = '';
      line('');
    },
  };
}

/** Callbacks and cancellation for {@link fluxAgentApi.streamMessage}. */
export interface FluxAgentStreamHandlers {
  /**
   * Answer text as it arrives. `round` increases when a model round that already sent text turns
   * out to call tools; replace provisional text when it changes.
   */
  onDelta?: (text: string, round: number) => void;
  /** A tool action completed while the reply is being produced. */
  onAction?: (action: FluxAgentAction) => void;
  /** A create or edit form changed while the agent is writing its arguments. */
  onForm?: (form: FluxAgentLiveForm) => void;
  /** Aborting stops reading; the server still finishes and stores the turn. */
  signal?: AbortSignal;
  /** Voice prosody JSON of the utterance this message was transcribed from, sent with the body. */
  prosody?: unknown;
  /** Ids of uploaded photos ({@link FluxAgentMedia}) attached to the message. */
  mediaIds?: string[];
}

/** Longest gap between streamed events (including keep-alives) before the client gives up. */
const STREAM_IDLE_MS = 60_000;

/** Credentialed headers matching the shared `request()` helper for a mutation. */
function authHeaders(contentType: string, accept: string): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': contentType,
    Accept: accept,
    'X-Fluxability-CSRF': '1',
  };
  try {
    const token = localStorage.getItem('flexiplanner-session-token');
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch {
    // Cookie authentication still applies.
  }
  return headers;
}

/** Fold a non-2xx response's JSON error envelope into a failed {@link FluxAgentResult}. */
async function failure<T>(response: Response): Promise<FluxAgentResult<T>> {
  const body = await response.json().catch(() => null);
  return toFluxAgentResult<T>({
    success: false,
    status: response.status,
    error: body?.error || { code: 'UNKNOWN_ERROR', message: 'An error occurred' },
  });
}

/** True when a stream request failure means the route is unavailable, so `send_message` is safe. */
function streamUnavailable(result: FluxAgentResult<unknown>): boolean {
  return (
    !result.ok &&
    (result.status === 405 ||
      (result.status === 404 && result.code !== 'NOT_FOUND') ||
      result.code === 'STREAM_UNSUPPORTED')
  );
}

/**
 * POST a streaming turn route (`/conversations/:id/messages/stream` by default, or `rerun/stream`)
 * and read its events. Resolves with the `done`
 * payload, the server's `error` event, or a request failure; never throws. Failures that happen
 * before the server accepted the turn and mean streaming itself is unavailable (route missing,
 * no response body, or a network error before any response) are reported with code
 * `STREAM_UNSUPPORTED` so the caller can fall back to `sendMessage` without duplicating the
 * message. A connection lost mid-stream is `STREAM_INTERRUPTED`: the turn still completes on
 * the server, so reload the conversation rather than resending.
 */
async function streamTurn(
  id: string,
  body: unknown,
  handlers: FluxAgentStreamHandlers,
  route: 'messages/stream' | 'rerun/stream' = 'messages/stream'
): Promise<FluxAgentResult<FluxAgentTurn>> {
  const idle = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const touch = () => {
    clearTimeout(timer);
    timer = setTimeout(() => idle.abort(new DOMException('Idle', 'TimeoutError')), STREAM_IDLE_MS);
  };
  // Combine manually: `AbortSignal.any` is missing before Safari 17.4.
  const forward = () => idle.abort(handlers.signal?.reason);
  if (handlers.signal?.aborted) forward();
  else handlers.signal?.addEventListener('abort', forward, { once: true });
  const signal = idle.signal;
  touch();
  let response: Response;
  try {
    response = await fetch(
      `${API_BASE}/flux-agent/conversations/${encodeURIComponent(id)}/${route}`,
      {
        method: 'POST',
        headers: authHeaders('application/json', 'text/event-stream'),
        body: JSON.stringify(body),
        credentials: 'include',
        signal,
      }
    );
  } catch (error) {
    clearTimeout(timer);
    if (handlers.signal?.aborted)
      return { ok: false, code: 'ABORTED', message: 'The request was cancelled.' };
    return {
      ok: false,
      code: 'STREAM_UNSUPPORTED',
      message: error instanceof Error ? error.message : 'Network error',
    };
  }
  if (!response.ok) {
    clearTimeout(timer);
    return failure(response);
  }
  if (!response.body || !response.headers.get('content-type')?.includes('text/event-stream')) {
    clearTimeout(timer);
    return { ok: false, code: 'STREAM_UNSUPPORTED', message: 'Streaming is unavailable.' };
  }
  let outcome: FluxAgentResult<FluxAgentTurn> | null = null;
  const parser = createSseParser(({ event, data }) => {
    let payload: unknown;
    try {
      payload = JSON.parse(data);
    } catch {
      return;
    }
    // Unchecked server JSON: `delta` and `action` frames are read field by field, while `done`
    // and `error` frames are trusted to carry the turn and error shapes the route serializes.
    const frame = payload as {
      /** Text fragment of a `delta` frame. */ text?: unknown;
      /** Tool round of a `delta` frame. */ round?: unknown;
      /** Completed tool action of an `action` frame. */ action?: FluxAgentAction;
      /** Tool-call slot of a `form` frame. */ slot?: unknown;
      /** Live form of a `form` frame. */ form?: FluxAgentForm;
    } | null;
    if (event === 'delta' && typeof frame?.text === 'string')
      handlers.onDelta?.(frame.text, Number(frame.round) || 0);
    else if (event === 'action' && frame?.action) handlers.onAction?.(frame.action);
    else if (event === 'form' && typeof frame?.slot === 'string' && frame.form)
      handlers.onForm?.({ slot: frame.slot, form: frame.form });
    else if (event === 'done')
      outcome = toFluxAgentResult<FluxAgentTurn>({ success: true, data: payload as FluxAgentTurn });
    else if (event === 'error')
      outcome = toFluxAgentResult<FluxAgentTurn>({
        success: false,
        status: 200,
        error: payload as FluxAgentEnvelope<FluxAgentTurn>['error'],
      });
  });
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      touch();
      parser.push(decoder.decode(value, { stream: true }));
      if (outcome) break;
    }
    parser.push(decoder.decode());
    parser.finish();
  } catch {
    // Reported below as an interrupted stream.
  } finally {
    clearTimeout(timer);
    handlers.signal?.removeEventListener('abort', forward);
    reader.cancel().catch(() => undefined);
  }
  return (
    outcome ?? {
      ok: false,
      code: 'STREAM_INTERRUPTED',
      message: handlers.signal?.aborted
        ? 'The request was cancelled.'
        : 'The connection dropped while Flux Agent was replying. Its reply will appear when you reopen the conversation.',
    }
  );
}

/**
 * POST raw bytes (or JSON) to a speech route with credentials and return the response, or a
 * failed result for network errors and non-2xx statuses.
 */
async function speechFetch(
  endpoint: string,
  body: BodyInit,
  contentType: string,
  accept: string,
  signal?: AbortSignal
): Promise<Response | FluxAgentResult<never>> {
  try {
    const response = await fetch(`${API_BASE}/flux-agent${endpoint}`, {
      method: 'POST',
      headers: authHeaders(contentType, accept),
      body,
      credentials: 'include',
      signal,
    });
    return response.ok ? response : failure<never>(response);
  } catch (error) {
    return {
      ok: false,
      code: signal?.aborted ? 'ABORTED' : 'NETWORK_ERROR',
      message: error instanceof Error ? error.message : 'Network error',
    };
  }
}

/**
 * Flux Agent route methods. Each resolves to a {@link FluxAgentResult} and never throws for HTTP
 * or network failures.
 */
export const fluxAgentApi = {
  /** GET `/status`: provider availability, trainer flag, voice support and limits. */
  status: () => call<FluxAgentStatus>('/status'),

  /** GET `/conversations`: the signed-in user's 50 most recently updated conversations. */
  listConversations: () => call<{ conversations: FluxAgentConversation[] }>('/conversations'),
  /** POST `/conversations` with `{ mode }`; defaults to a coaching conversation. */
  createConversation: (mode: FluxAgentMode = 'coach') =>
    call<{ conversation: FluxAgentConversation }>('/conversations', json('POST', { mode })),
  /** GET `/conversations/:id`: the summary plus its visible user and assistant messages. */
  getConversation: (id: string) =>
    call<{ conversation: FluxAgentConversation; messages: FluxAgentMessage[] }>(
      `/conversations/${encodeURIComponent(id)}`
    ),
  /** DELETE `/conversations/:id`, removing the conversation and its full transcript. */
  deleteConversation: (id: string) =>
    call<{ deleted: true }>(`/conversations/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  /** Send a message; resolves with the stored user message followed by the assistant reply. */
  sendMessage: (
    id: string,
    content: string,
    fileIds: string[] = [],
    timezone = currentTimeZone(),
    prosody?: unknown,
    mediaIds: string[] = []
  ) =>
    call<{ conversation: FluxAgentConversation; messages: FluxAgentMessage[] }>(
      `/conversations/${encodeURIComponent(id)}/messages`,
      {
        ...json('POST', {
          content,
          timezone,
          ...(fileIds.length ? { fileIds } : {}),
          ...(mediaIds.length ? { mediaIds } : {}),
          ...(prosody ? { prosody } : {}),
        }),
        signal: AbortSignal.timeout(MESSAGE_TIMEOUT_MS),
      }
    ),

  /**
   * Send a message and stream the reply: `onDelta` receives answer text as it is generated and
   * `onAction` each completed tool action. Resolves with the same payload as {@link sendMessage}.
   * When streaming is unavailable (route missing, no readable body, or a network failure before
   * any response) it falls back to {@link sendMessage}; `streamed` tells which path answered.
   */
  streamMessage: async (
    id: string,
    content: string,
    fileIds: string[] = [],
    handlers: FluxAgentStreamHandlers = {},
    timezone = currentTimeZone()
  ): Promise<FluxAgentResult<FluxAgentTurn> & { streamed: boolean }> => {
    const { prosody, mediaIds = [] } = handlers;
    const body = {
      content,
      timezone,
      ...(fileIds.length ? { fileIds } : {}),
      ...(mediaIds.length ? { mediaIds } : {}),
      ...(prosody ? { prosody } : {}),
    };
    const streamed = await streamTurn(id, body, handlers);
    if (!streamUnavailable(streamed)) return { ...streamed, streamed: true };
    return {
      ...(await fluxAgentApi.sendMessage(id, content, fileIds, timezone, prosody, mediaIds)),
      streamed: false,
    };
  },

  /**
   * POST `/conversations/:id/rerun/stream` (trainers): replace the reply to the conversation's last
   * message with one from the current system prompt and playbooks, streamed like
   * {@link streamMessage}. Resolves with the user message and the new reply.
   */
  rerunLastMessage: (
    id: string,
    handlers: FluxAgentStreamHandlers = {},
    timezone = currentTimeZone()
  ): Promise<FluxAgentResult<FluxAgentTurn>> =>
    streamTurn(id, { timezone }, handlers, 'rerun/stream'),

  /** POST `/conversations/:id/coach` (trainers): open or create the coaching side chat for a conversation. */
  openCoaching: (id: string) =>
    call<CoachingSession>(`/conversations/${encodeURIComponent(id)}/coach`, json('POST', {})),
  /** GET `/conversations/:coachingId/proposals`: the side chat's staged changes with diffs. */
  listProposals: (coachingId: string) =>
    call<{ proposals: CoachingProposal[] }>(
      `/conversations/${encodeURIComponent(coachingId)}/proposals`
    ),
  /** POST `/conversations/:coachingId/proposals/accept`: commit every staged change in one commit. */
  acceptProposals: (coachingId: string) =>
    call<{ commit: string | null; paths: string[] }>(
      `/conversations/${encodeURIComponent(coachingId)}/proposals/accept`,
      json('POST', {})
    ),
  /**
   * POST `/actions/:id/confirm`: run an action Flux Agent staged as a confirmation card. Resolves
   * with the card as it ends (`completed` or `failed`); 409 when it was already decided or expired.
   */
  confirmAction: (pendingId: string) =>
    call<{ action: FluxAgentAction }>(
      `/actions/${encodeURIComponent(pendingId)}/confirm`,
      json('POST', {})
    ),
  /** POST `/actions/:id/cancel`: discard a staged action without running it. */
  cancelAction: (pendingId: string) =>
    call<{ action: FluxAgentAction }>(
      `/actions/${encodeURIComponent(pendingId)}/cancel`,
      json('POST', {})
    ),
  /** DELETE `/conversations/:coachingId/proposals`: discard every staged change. */
  discardProposals: (coachingId: string) =>
    call<{ discarded: number }>(`/conversations/${encodeURIComponent(coachingId)}/proposals`, {
      method: 'DELETE',
    }),

  /** GET `/files`: the user's uploaded files (extracted text only, no original bytes). */
  listFiles: () => call<{ files: FluxAgentFile[] }>('/files'),
  /**
   * POST `/files` with `{ name, mimeType, dataBase64 }`. The server extracts text and keeps only
   * that; uploads over `limits.maxFileBytes` are rejected.
   */
  uploadFile: (name: string, mimeType: string, dataBase64: string) =>
    call<{ file: FluxAgentFile }>('/files', json('POST', { name, mimeType, dataBase64 })),
  /** DELETE `/files/:id`. Messages that already referenced the file keep their name chip. */
  deleteFile: (id: string) =>
    call<{ deleted: true }>(`/files/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  /**
   * POST a photo's raw bytes to `/media/photos` with its type (`image/jpeg` or `image/webp`) as
   * `Content-Type`. The server checks the magic bytes, strips metadata and stores it unattached
   * until a message carries its id; one never sent is deleted after a day.
   */
  uploadPhoto: async (photo: Blob): Promise<FluxAgentResult<{ media: FluxAgentMedia }>> => {
    const response = await speechFetch(
      '/media/photos',
      photo,
      photo.type || 'image/jpeg',
      'application/json'
    );
    if (!(response instanceof Response)) return response;
    return toFluxAgentResult(await response.json().catch(() => ({ success: false })));
  },
  /** GET `/media`: the user's newest photos and clips, optionally of one kind. */
  listMedia: (kind?: 'photo' | 'audio') =>
    call<{ media: FluxAgentMedia[] }>(kind ? `/media?kind=${kind}` : '/media'),
  /** DELETE `/media/:id`: remove a photo or clip and its stored bytes. */
  deleteMedia: (id: string) =>
    call<{ deleted: true }>(`/media/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  /** POST `/speech/transcribe` with base64 audio and its MIME type; resolves with the text. */
  transcribe: (audioBase64: string, mimeType: string) =>
    call<{ text: string }>('/speech/transcribe', json('POST', { audioBase64, mimeType })),
  /**
   * POST the recording's raw bytes to `/speech/transcribe` with its media type as `Content-Type`
   * (no base64 inflation); resolves with the text.
   */
  transcribeAudio: async (
    audio: Blob,
    mimeType: string
  ): Promise<FluxAgentResult<{ text: string }>> => {
    const response = await speechFetch(
      '/speech/transcribe',
      audio,
      mimeType || audio.type || 'application/octet-stream',
      'application/json'
    );
    if (!(response instanceof Response)) return response;
    return toFluxAgentResult(await response.json().catch(() => ({ success: false })));
  },
  /**
   * POST one sentence (at most 400 characters) to `/speech/synthesize/stream`; resolves with the
   * MP3 bytes. Aborting cancels the request.
   */
  synthesizeAudio: async (
    text: string,
    signal?: AbortSignal
  ): Promise<FluxAgentResult<ArrayBuffer>> => {
    const response = await speechFetch(
      '/speech/synthesize/stream',
      JSON.stringify({ text }),
      'application/json',
      'audio/mpeg',
      signal
    );
    if (!(response instanceof Response)) return response;
    try {
      return { ok: true, data: await response.arrayBuffer() };
    } catch (error) {
      return {
        ok: false,
        code: signal?.aborted ? 'ABORTED' : 'NETWORK_ERROR',
        message: error instanceof Error ? error.message : 'Audio download failed.',
      };
    }
  },
  /** POST `/speech/synthesize` with `{ text }`; resolves with base64 audio and its MIME type. */
  synthesize: (text: string) =>
    call<{ audioBase64: string; mimeType: string }>('/speech/synthesize', json('POST', { text })),

  /** GET `/memory`: the user's own `memory/` documents. */
  listMemory: () => call<{ documents: FluxAgentDocument[] }>('/memory'),
  /**
   * PUT `/memory` with `{ path, content, expectedVersion }`. Omitting `expectedVersion` overwrites
   * without a conflict check.
   */
  saveMemory: (path: string, content: string, expectedVersion?: number) =>
    call<{ document: FluxAgentDocument }>(
      '/memory',
      json('PUT', { path, content, expectedVersion })
    ),
  /** DELETE `/memory?path=` for one of the user's memory documents. */
  deleteMemory: (path: string) =>
    call<{ deleted: true }>(`/memory${pathQuery(path)}`, { method: 'DELETE' }),

  /**
   * Trainer-only: the secret system prompt, whether it is still the built-in default, and the
   * library commit it was read from.
   */
  getSystemPrompt: () =>
    call<{ document: LibraryDocument; isDefault: boolean; commit: string | null }>(
      '/admin/system-prompt'
    ),
  /** Trainer-only: PUT `/admin/system-prompt`, committing the content to `main`. */
  saveSystemPrompt: (save: LibraryDocumentSave) =>
    call<{ document: LibraryDocument; commit: string | null }>(
      '/admin/system-prompt',
      json('PUT', save)
    ),
  /** Trainer-only: GET `/admin/system-prompt/history`, newest commit first. */
  systemPromptHistory: () => call<{ revisions: LibraryRevision[] }>('/admin/system-prompt/history'),
  /** Trainer-only: GET `/admin/playbooks`, the global playbooks at the latest commit. */
  listPlaybooks: () =>
    call<{ documents: LibraryDocument[]; commit: string | null }>('/admin/playbooks'),
  /** Trainer-only: PUT `/admin/playbooks` with `{ path, ...save }` to create or update one. */
  savePlaybook: (path: string, save: LibraryDocumentSave) =>
    call<{ document: LibraryDocument; commit: string | null }>(
      '/admin/playbooks',
      json('PUT', { path, ...save })
    ),
  /**
   * Trainer-only: DELETE `/admin/playbooks?path=`, committing the removal. A non-empty
   * `expectedRevision` makes the delete fail with a conflict if the playbook changed meanwhile.
   */
  deletePlaybook: (path: string, expectedRevision?: string) =>
    call<{ deleted: true; commit: string | null }>(
      `/admin/playbooks${pathQuery(path)}${
        expectedRevision ? `&expectedRevision=${encodeURIComponent(expectedRevision)}` : ''
      }`,
      { method: 'DELETE' }
    ),
  /** Trainer-only: GET `/admin/playbooks/history?path=`, newest commit first. */
  playbookHistory: (path: string) =>
    call<{ revisions: LibraryRevision[] }>(`/admin/playbooks/history${pathQuery(path)}`),
  /**
   * Trainer-only: POST `/admin/playbooks/import`, written as one commit. With `replace` (default
   * false), playbooks absent from the import are deleted. Resolves with the written and removed
   * counts and the new commit.
   */
  importPlaybooks: (documents: { path: string; content: string }[], replace = false) =>
    call<{ imported: number; removed: number; commit: string | null }>(
      '/admin/playbooks/import',
      json('POST', { documents, replace })
    ),
  /** Trainer-only: GET `/admin/library`, the Git repository remote, branch and latest commit. */
  libraryInfo: () => call<LibraryInfo>('/admin/library'),
  /**
   * Trainer-only: POST `/admin/library/token` for a one-hour Git token. Keep the token in memory
   * only; it is a secret.
   */
  createLibraryToken: (scope: LibraryTokenScope) =>
    call<LibraryToken>('/admin/library/token', json('POST', { scope })),
};
